// :cow: Cosmose CONFIDENTIAL :iso:
import { AfterViewInit, Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, Output, ViewChild } from '@angular/core';
import { ElementPosition } from '@med/app/home/demo/guided-tour/services/element-position.service';
import { interval, Subject } from 'rxjs';
import { debounceTime, filter, map, take, takeUntil } from 'rxjs/operators';
import { ElementDimensionObserverAggregateService } from '@med/app/home/demo/guided-tour/services/element-dimension-observer-aggregate.service';
import { StepExposedElement } from '@med/app/home/demo/guided-tour/models/step';

@Component({
  selector: 'e-exposed-element',
  templateUrl: './exposed-element.component.html',
  styleUrls: ['./exposed-element.component.scss'],
  providers: [ElementDimensionObserverAggregateService],
})
export class ExposedElementComponent implements OnChanges, OnDestroy, AfterViewInit {

  @Input() stepExposedElement: StepExposedElement;
  @Output() exposedElementClick = new EventEmitter<void>();

  @ViewChild('portal', {static: false}) portal: ElementRef<HTMLElement>;
  @ViewChild('portalTop', {static: false}) portalTop: ElementRef<HTMLElement>;
  @ViewChild('portalLeft', {static: false}) portalLeft: ElementRef<HTMLElement>;
  @ViewChild('portalRight', {static: false}) portalRight: ElementRef<HTMLElement>;
  @ViewChild('portalBottom', {static: false}) portalBottom: ElementRef<HTMLElement>;

  private exposedElement: HTMLElement;
  private parentElement: HTMLElement;
  private unsubscribe$ = new Subject<void>();

  constructor(
    private elementPosition: ElementPosition,
    private dimensionObserverService: ElementDimensionObserverAggregateService,
  ) {
  }

  ngAfterViewInit(): void {
    this.watchDimensionChanges();

    if (this.portal) {
      this.redraw();
    }
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
    this.dimensionObserverService.removeAllObservers();
  }

  ngOnChanges(): void {
    if (this.portal) {
      this.redraw();
    }
  }

  onExposedElementClick(): void {
    this.exposedElementClick.emit();
  }

  private redraw(): void {
    this.dimensionObserverService.unobserve('element');
    this.dimensionObserverService.unobserve('parentElement');
    if (this.exposedElement) {
      this.exposedElement.removeEventListener('click', this.onExposedElementClick.bind(this));
    }

    if (!this.stepExposedElement) {
      return;
    }

    interval(100).pipe(
      map(() => document.querySelector(this.stepExposedElement.selector)),
      filter(v => v != null),
      takeUntil(this.unsubscribe$),
      take(1),
    ).subscribe((element: HTMLElement) => {
      this.exposedElement = element;
      this.exposedElement.addEventListener('click', this.onExposedElementClick.bind(this));
      this.parentElement = document.body.parentElement;
      this.dimensionObserverService.observe('element', this.exposedElement);
      this.dimensionObserverService.observe('parentElement', this.parentElement);
      this.showExposedElement();
    });
  }

  private showExposedElement(): void {
    const dimensions = this.exposedElement.getBoundingClientRect();
    const parentElementRect = this.parentElement.getBoundingClientRect();

    this.elementPosition.setElementPosition(
      this.portal.nativeElement,
      dimensions.top - parentElementRect.top,
      dimensions.left,
      dimensions.width,
      dimensions.height,
    );
    this.elementPosition.setElementPosition(
      this.portalTop.nativeElement,
      0,
      0,
      parentElementRect.width,
      dimensions.top - parentElementRect.top,
    );
    this.elementPosition.setElementPosition(
      this.portalBottom.nativeElement,
      dimensions.top + dimensions.height - parentElementRect.top,
      0,
      parentElementRect.width,
      parentElementRect.height - dimensions.top - dimensions.height + parentElementRect.top,
    );
    this.elementPosition.setElementPosition(
      this.portalLeft.nativeElement,
      dimensions.top - parentElementRect.top,
      0,
      dimensions.left,
      dimensions.height,
    );
    this.elementPosition.setElementPosition(
      this.portalRight.nativeElement,
      dimensions.top - parentElementRect.top,
      dimensions.left + dimensions.width,
      parentElementRect.width - dimensions.left - dimensions.width,
      dimensions.height,
    );
  }

  private watchDimensionChanges(): void {
    this.dimensionObserverService.allEvents$.pipe(
      debounceTime(100),
      takeUntil(this.unsubscribe$),
    ).subscribe(() => {
      this.showExposedElement();
    });
  }
}
